import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import { Trash2, Users, Mail, CreditCard, Activity, Briefcase, Shield } from "lucide-react";

export default function Admin() {
  const { user, ready } = useAuth();
  const { t } = useLang();
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [tab, setTab] = useState("users");

  const load = async () => {
    try {
      const [s, c, u, p] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/contacts"),
        api.get("/admin/users"),
        api.get("/admin/payments"),
      ]);
      setStats(s.data);
      setContacts(c.data.contacts || []);
      setUsers(u.data.users || []);
      setPayments(p.data.payments || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (!ready) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const removeContact = async (id) => {
    try {
      await api.delete(`/admin/contacts/${id}`);
      setContacts(contacts.filter((x) => x.id !== id));
      toast.success("Silindi");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const removeUser = async (id) => {
    if (!window.confirm(t.admin.confirm_delete)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      setUsers(users.filter((x) => x.id !== id));
      toast.success("Kullanıcı silindi");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const updateUser = async (id, patch) => {
    try {
      await api.patch(`/admin/users/${id}`, patch);
      setUsers(users.map((u) => u.id === id ? { ...u, ...patch } : u));
      toast.success("Güncellendi");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cards = [
    { icon: Users, key: "users", label: t.admin.total_users },
    { icon: Briefcase, key: "paid_users", label: t.admin.paid_users },
    { icon: Mail, key: "contacts", label: t.admin.total_contacts },
    { icon: CreditCard, key: "payments", label: t.admin.total_payments },
    { icon: Activity, key: "service_calls", label: t.admin.total_calls },
  ];
  const tabs = [
    { id: "users", label: t.admin.users_title, icon: Users },
    { id: "payments", label: t.admin.payments_title, icon: CreditCard },
    { id: "contacts", label: t.admin.contacts_title, icon: Mail },
  ];

  return (
    <div className="min-h-screen flex flex-col" data-testid="admin-page">
      <Header />
      <main className="flex-1 max-w-7xl w-full mx-auto px-5 md:px-8 py-12 md:py-16">
        <div className="label-eyebrow mb-4">— {t.admin.title}</div>
        <h1 className="font-serif-display text-4xl md:text-5xl tracking-tighter mb-12">{t.admin.title}</h1>

        <section className="mb-14">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {cards.map((c) => (
              <div key={c.key} className="aq-card p-5" data-testid={`admin-stat-${c.key}`}>
                <c.icon size={18} className="text-[#1E3A5F]" />
                <div className="font-serif-display text-3xl md:text-4xl tracking-tighter mt-3">
                  {stats ? stats[c.key] : "—"}
                </div>
                <div className="font-mono-aq text-[0.65rem] tracking-[0.2em] uppercase text-[#4A4A4A] mt-2">{c.label}</div>
              </div>
            ))}
          </div>
        </section>

        <div className="flex flex-wrap gap-2 mb-6 border-b border-[#B6B09F]/40">
          {tabs.map((tb) => (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              className={`flex items-center gap-2 px-4 py-3 font-mono-aq text-[0.7rem] uppercase tracking-[0.2em] border-b-2 transition-colors ${tab === tb.id ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-[#4A4A4A] hover:text-[#0A0A0A]"}`}
              data-testid={`admin-tab-${tb.id}`}
            >
              <tb.icon size={14} /> {tb.label}
            </button>
          ))}
        </div>

        {tab === "users" && (
          <section className="aq-card overflow-hidden" data-testid="admin-users">
            {users.length === 0 ? (
              <div className="p-6 text-sm text-[#4A4A4A]">{t.admin.empty}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#EAE4D5]">
                    <tr className="font-mono-aq text-[0.65rem] tracking-[0.2em] uppercase text-[#4A4A4A]">
                      <th className="text-left p-3">{t.contact.name}</th>
                      <th className="text-left p-3">{t.contact.email}</th>
                      <th className="text-left p-3">{t.admin.role}</th>
                      <th className="text-left p-3">{t.admin.plan}</th>
                      <th className="text-left p-3">{t.admin.joined}</th>
                      <th className="text-right p-3">{t.admin.actions}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#B6B09F]/30">
                    {users.map((u) => (
                      <tr key={u.id} data-testid={`admin-user-${u.id}`}>
                        <td className="p-3">{u.name}</td>
                        <td className="p-3">{u.email}</td>
                        <td className="p-3">
                          <select value={u.role} onChange={(e) => updateUser(u.id, { role: e.target.value })}
                            disabled={u.id === user.id}
                            className="bg-transparent border border-[#B6B09F] rounded px-2 py-1 text-xs"
                            data-testid={`role-select-${u.id}`}>
                            <option value="user">user</option>
                            <option value="admin">admin</option>
                          </select>
                        </td>
                        <td className="p-3">
                          <select value={u.plan} onChange={(e) => updateUser(u.id, { plan: e.target.value })}
                            className="bg-transparent border border-[#B6B09F] rounded px-2 py-1 text-xs"
                            data-testid={`plan-select-${u.id}`}>
                            <option value="free">free</option>
                            <option value="starter">starter</option>
                            <option value="pro">pro</option>
                            <option value="enterprise">enterprise</option>
                          </select>
                        </td>
                        <td className="p-3 text-[#4A4A4A]">{new Date(u.created_at).toLocaleDateString()}</td>
                        <td className="p-3 text-right">
                          <button onClick={() => removeUser(u.id)} disabled={u.id === user.id}
                            className="text-[#0A0A0A] hover:text-red-700 disabled:opacity-30 disabled:cursor-not-allowed"
                            aria-label={t.admin.delete} data-testid={`delete-user-${u.id}`}>
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {tab === "payments" && (
          <section className="aq-card overflow-hidden" data-testid="admin-payments">
            {payments.length === 0 ? (
              <div className="p-6 text-sm text-[#4A4A4A]">{t.admin.empty}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#EAE4D5]">
                    <tr className="font-mono-aq text-[0.65rem] tracking-[0.2em] uppercase text-[#4A4A4A]">
                      <th className="text-left p-3">{t.contact.email}</th>
                      <th className="text-left p-3">{t.admin.plan}</th>
                      <th className="text-left p-3">{t.admin.amount}</th>
                      <th className="text-left p-3">{t.admin.status}</th>
                      <th className="text-left p-3">{t.admin.date}</th>
                      <th className="text-left p-3">{t.admin.session}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#B6B09F]/30">
                    {payments.map((p) => (
                      <tr key={p.id}>
                        <td className="p-3">{p.user_email}</td>
                        <td className="p-3 capitalize">{p.plan_id}</td>
                        <td className="p-3">${p.amount?.toFixed(2)} {(p.currency || "").toUpperCase()}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-xs ${p.payment_status === "paid" ? "bg-[#1E3A5F] text-[#EAE4D5]" : "bg-[#EAE4D5] text-[#0A0A0A]"}`}>
                            {p.payment_status || "—"}
                          </span>
                        </td>
                        <td className="p-3 text-[#4A4A4A]">{new Date(p.created_at).toLocaleString()}</td>
                        <td className="p-3 font-mono-aq text-xs text-[#4A4A4A] truncate max-w-[180px]">{p.session_id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {tab === "contacts" && (
          <section className="aq-card overflow-hidden" data-testid="admin-contacts">
            {contacts.length === 0 ? (
              <div className="p-6 text-sm text-[#4A4A4A]">{t.admin.empty}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#EAE4D5]">
                    <tr className="font-mono-aq text-[0.65rem] tracking-[0.2em] uppercase text-[#4A4A4A]">
                      <th className="text-left p-3">{t.contact.name}</th>
                      <th className="text-left p-3">{t.contact.email}</th>
                      <th className="text-left p-3 hidden md:table-cell">{t.contact.message}</th>
                      <th className="text-right p-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#B6B09F]/30">
                    {contacts.map((c) => (
                      <tr key={c.id} data-testid={`admin-contact-${c.id}`}>
                        <td className="p-3">{c.name}<div className="text-xs text-[#B6B09F]">{c.company || "—"}</div></td>
                        <td className="p-3">{c.email}</td>
                        <td className="p-3 hidden md:table-cell max-w-md truncate">{c.message}</td>
                        <td className="p-3 text-right">
                          <button onClick={() => removeContact(c.id)} className="text-[#0A0A0A] hover:text-red-700" aria-label={t.admin.delete} data-testid={`delete-contact-${c.id}`}>
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
